/**
 * 帖子媒体（图文 / 视频）的公共口径，和 App 端 pp-front-uniapp/utils/articleMedia.js 同一套规则。
 *
 * 一条帖子要么是图文（最多 9 张图），要么是一个视频（小红书的做法）。
 * 后端字段（pp-back 的 ArticleMediaConstants / ApiArticleListVO）：
 *   mediaType      0 图文，1 视频
 *   videoUrl       视频 CDN 地址。转码完成后是转码版（H.264 720p），否则是原片
 *   videoOriginUrl 只有 videoUrl 已经换成转码版时才有值，播放转码版出错时回退到它
 *   videoDuration  时长，毫秒
 *   videoWidth / videoHeight  展示宽高（已按旋转校正）
 *   avatar / avatarList       视频帖时只有一张，是封面
 *
 * 封面放在 avatar 里是为了兼容：不认识 mediaType 的老客户端会把封面当成一张配图展示，
 * 而不是一条空白的纯文字帖。所以新代码判断视频帖一律走 isVideoArticle，不要看 avatarList。
 */

export const MEDIA_TYPE_IMAGE_TEXT = 0
export const MEDIA_TYPE_VIDEO = 1

/** 视频上传目录：服务端会在后面追加 {登录账号id}/{yyyyMMdd}/，发帖时据此校验归属 */
export const VIDEO_UPLOAD_DIRECTORY = 'article/video'

/** 视频封面和图文配图同一个目录：服务端校验封面 key 必须是 article/{yyyyMMdd}/xxx.jpg */
export const VIDEO_COVER_DIRECTORY = 'article'

/** 与服务端 ArticleMediaConstants.VIDEO_MAX_SIZE_BYTES 一致 */
export const VIDEO_MAX_SIZE_BYTES = 200 * 1024 * 1024

/** 选视频时的时长上限（毫秒）。服务端只做 30 分钟的合理性兜底，真正的产品限制在这里（与 App 一致） */
export const VIDEO_MAX_DURATION_MS = 5 * 60 * 1000

export const VIDEO_MIN_DURATION_MS = 1000

/**
 * 服务端只放行这三种扩展名（ArticleVideoSupport.VIDEO_FILE_NAME）。
 * webm / avi 之类即使浏览器能播，传上去发帖也会被拒，所以选择器里就不给选。
 */
export const VIDEO_EXTENSIONS = ['mp4', 'mov', 'm4v']

/** <input type="file"> 的 accept：MIME 和扩展名都写，部分 macOS / Safari 只认扩展名 */
export const VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/x-m4v,.mp4,.mov,.m4v'

/** 这条帖子是不是视频帖（必须真的有视频地址，脏数据按图文兜底） */
export function isVideoArticle (article) {
  return !!article && Number(article.mediaType) === MEDIA_TYPE_VIDEO && !!article.videoUrl
}

/** 视频封面：avatarList 第一张，没有时退回 avatar 字符串的第一段 */
export function videoCoverOf (article) {
  if (!article) return ''
  if (Array.isArray(article.avatarList) && article.avatarList.length) {
    return article.avatarList[0] || ''
  }
  if (typeof article.avatar === 'string' && article.avatar) {
    return article.avatar.split(',')[0] || ''
  }
  return ''
}

function pad2 (n) {
  return n < 10 ? '0' + n : String(n)
}

/** 时长（毫秒）格式化成 0:15 / 12:03 / 1:02:03，拿不到时长时返回空串 */
export function formatVideoDuration (ms) {
  const total = Math.max(0, Math.round((Number(ms) || 0) / 1000))
  if (!total) return ''
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${m}:${pad2(s)}`
}

/** 播放进度用的 00:12 格式（秒） */
export function formatPlayTime (seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${pad2(m)}:${pad2(s)}`
}

/** 视频画面的高宽比（height / width），拿不到宽高时返回 0 */
export function videoAspectRatio (article) {
  const w = Number(article && article.videoWidth) || 0
  const h = Number(article && article.videoHeight) || 0
  if (w <= 0 || h <= 0) return 0
  return h / w
}

/**
 * 横版 / 方形 / 竖版，列表卡片和发布页的视频块据此决定版式。
 * 不知道宽高（老数据）时按竖版处理：手机拍的视频绝大多数是竖的。
 * 阈值和 App 端 feedVideoLayout 一致。
 */
export function videoOrientation (article) {
  const ratio = videoAspectRatio(article)
  if (ratio && ratio < 0.87) return 'landscape'
  if (ratio && ratio <= 1.15) return 'square'
  return 'portrait'
}

/**
 * 播放页里这条视频要不要铺满画面（裁边），和抖音、小红书一致：
 * 竖屏视频铺满后被裁掉的部分不超过 20% 才铺满；横屏、方形或者裁得太多的完整显示，
 * 上下（或左右）留出的地方用封面的模糊底图填。
 *
 * @returns {Boolean} 拿不到宽高时返回 false（完整显示，至少不会裁掉东西）
 */
export function shouldFillStage (videoWidth, videoHeight, stageWidth, stageHeight) {
  const vw = Number(videoWidth) || 0
  const vh = Number(videoHeight) || 0
  const sw = Number(stageWidth) || 0
  const sh = Number(stageHeight) || 0
  if (!vw || !vh || !sw || !sh || vh <= vw) return false
  const videoRatio = vw / vh
  const stageRatio = sw / sh
  const cropped = videoRatio > stageRatio ? 1 - stageRatio / videoRatio : 1 - videoRatio / stageRatio
  return cropped <= 0.2
}

// ==================== 列表 → 播放器的首屏数据交接 ====================
//
// 播放器如果只拿到 id，要等详情接口回来才能画出封面、作者和文案，点进去会先黑一下。
// 列表手上已经有完整的帖子数据，打开前先放进这里，播放器取走后首帧就是完整画面，
// 详情接口回来后再校正点赞 / 关注这些状态。
// 只保留最近几条、过期即丢，免得一直持有列表里的大对象。

const ENTRY_TTL_MS = 60 * 1000
const ENTRY_MAX = 5
const entryCache = new Map()

export function stashVideoEntry (article) {
  if (!article || !article.id) return
  const key = String(article.id)
  entryCache.delete(key)
  entryCache.set(key, { article: { ...article }, at: Date.now() })
  while (entryCache.size > ENTRY_MAX) {
    const oldest = entryCache.keys().next().value
    entryCache.delete(oldest)
  }
}

export function takeVideoEntry (id) {
  if (id === undefined || id === null || id === '') return null
  const key = String(id)
  const entry = entryCache.get(key)
  entryCache.delete(key)
  if (!entry || Date.now() - entry.at > ENTRY_TTL_MS) return null
  return entry.article
}

/**
 * 消息通知（评论和@、点赞、收藏）里关联的帖子，转成 isVideoArticle 认识的帖子对象。
 *
 * 通知接口除了 articleId / articleTitle / articleContent，还带了 articleMediaType 和视频帖的
 * 封面、视频地址、宽高（pp-back ImMessageVO.fillArticleMedia）。视频帖据此直接打开播放器，
 * 不用先进图文详情、拉到详情才发现是视频再切过去。
 * 老版本服务端不带这些字段时 isVideoArticle 判不出视频，照旧走图文详情，由详情弹窗再切到播放器。
 *
 * @param {Object} notice 通知列表里的一条
 * @returns {Object|null} 没有关联帖子时返回 null
 */
export function articleFromNotice (notice) {
  if (!notice || !notice.articleId) return null
  return {
    id: notice.articleId,
    mediaType: notice.articleMediaType,
    videoUrl: notice.articleVideoUrl,
    avatar: notice.articleCover,
    videoWidth: notice.articleVideoWidth,
    videoHeight: notice.articleVideoHeight,
    title: notice.articleTitle,
    content: notice.articleContent
  }
}

// ==================== 私信分享卡片 ====================

/**
 * 分享到私信的卡片内容（messageType = 5）。
 *
 * 沿用帖子卡片的协议，多带 mediaType / cover：认识它们的客户端显示成视频卡片并直接打开播放器，
 * 老版本认不出也只是一张普通的帖子卡片。字段和 App 端 pages/video/index.vue 的 buildShareContent 完全一致，
 * 服务端 ShareCardUtils 也按 mediaType 生成「[分享视频]」的会话预览。
 */
export function buildVideoShareContent (article) {
  const author = (article && article.userInfoVO) || {}
  const text = String((article && (article.title || article.content)) || '').replace(/\s+/g, ' ').trim()
  return JSON.stringify({
    articleId: String(article.id),
    authorAvatar: author.avatar || '',
    authorName: author.nickname || '',
    content: text ? (text.length > 20 ? text.slice(0, 20) + '...' : text) : '[视频]',
    mediaType: MEDIA_TYPE_VIDEO,
    cover: videoCoverOf(article) || article.cover || ''
  })
}

/** 分享卡片是不是一条视频（群聊 / 用户 / 评论卡片都不是） */
export function isVideoShareCard (card) {
  if (!card || typeof card !== 'object') return false
  if (card.type === 'group' || card.type === 'user' || card.type === 'comment') return false
  return Number(card.mediaType) === MEDIA_TYPE_VIDEO && !!card.articleId
}

export default {
  MEDIA_TYPE_IMAGE_TEXT,
  MEDIA_TYPE_VIDEO,
  VIDEO_UPLOAD_DIRECTORY,
  VIDEO_COVER_DIRECTORY,
  VIDEO_MAX_SIZE_BYTES,
  VIDEO_MAX_DURATION_MS,
  VIDEO_MIN_DURATION_MS,
  VIDEO_EXTENSIONS,
  VIDEO_ACCEPT,
  isVideoArticle,
  videoCoverOf,
  formatVideoDuration,
  formatPlayTime,
  videoAspectRatio,
  videoOrientation,
  shouldFillStage,
  stashVideoEntry,
  takeVideoEntry,
  articleFromNotice,
  buildVideoShareContent,
  isVideoShareCard
}
